// ---------------------------------------------------------------------------
// Edit stages — one touch-friendly surface for the whole pipeline shape:
// add, rename, reorder (↑↓), delete-when-empty. Anyone on the board since
// v1.96.0 (the server enforces it too); the step-owner picker stays with the
// lead and admins. Every team models its own workflow here.
// ---------------------------------------------------------------------------

import { useEffect, useState } from 'react';
import { ChevronDown, ChevronUp, Plus, Trash2, X, Zap } from 'lucide-react';
import type { Stage } from '@cg-projects/shared';
import { Icon } from '@/core/ui/icons';
import { api } from '@/core/api/apiClient';
import { useAreaStore } from '@/features/areas/store/areaStore';
import { useToastStore } from '@/core/ui/toastStore';
import { useEscapeToClose } from '@/core/ui/useEscapeToClose';

export function StageManagerModal({ areaId, stages, taskCountByStage, canSetOwners, onClose }: {
  areaId: number;
  stages: Stage[];
  taskCountByStage: Map<number, number>;
  /** Lead or admin: the step-owner picker. Everyone else sees the owner, read-only. */
  canSetOwners: boolean;
  onClose: () => void;
}) {
  useEscapeToClose(onClose);
  const reloadStages = useAreaStore((s) => s.reloadStages);
  // The approve→advance engine resolves its pipeline by stage NAME, so renaming or
  // deleting one of these silently re-points where approvals land. Warn first.
  const areas = useAreaStore((s) => s.areas);
  const pipeline = new Set(
    (areas.find((a) => a.id === areaId)?.pipelineStages ?? []).map((n) => n.trim().toLowerCase()),
  );
  const inPipeline = (name: string) => pipeline.has(name.trim().toLowerCase());
  const toast = useToastStore((s) => s.show);
  const [busy, setBusy] = useState(false);
  const [addDraft, setAddDraft] = useState('');
  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameDraft, setRenameDraft] = useState('');
  // The relay's switch (v1.83.0): who owns each step. Members only — a step
  // can't belong to someone who can't see the board.
  const [members, setMembers] = useState<Array<{ id: number; name: string }>>([]);
  useEffect(() => {
    if (!canSetOwners) return;
    let dead = false;
    api.get<{ members: Array<{ id: number; name: string }> }>(`/areas/${areaId}/members`)
      .then((r) => { if (!dead) setMembers(r.members); })
      .catch(() => { /* picker just stays empty */ });
    return () => { dead = true; };
  }, [areaId, canSetOwners]);

  const setOwner = (stageId: number, ownerUid: number | null) => {
    void run(() => api.patch(`/areas/${areaId}/stages/${stageId}`, { ownerUid }), 'Could not set the step owner.');
  };

  const run = async (fn: () => Promise<unknown>, failMsg: string) => {
    if (busy) return;
    setBusy(true);
    try {
      await fn();
      await reloadStages(areaId);
    } catch (e) {
      toast(e instanceof Error ? e.message : failMsg, 'error');
    } finally {
      setBusy(false);
    }
  };

  const addStage = () => {
    const name = addDraft.trim();
    if (!name) return;
    void run(async () => {
      await api.post(`/areas/${areaId}/stages`, { name });
      setAddDraft('');
    }, 'Could not add the stage.');
  };

  const saveRename = (stageId: number) => {
    const name = renameDraft.trim();
    if (!name) { setRenamingId(null); return; }
    const was = stages.find((s) => s.id === stageId)?.name ?? '';
    if (inPipeline(was) && !inPipeline(name)) {
      const ok = window.confirm(
        `"${was}" is part of this team's automatic stage advancement.

` +
        `Renaming it to "${name}" will change where approvals send cards — the ` +
        `automation looks the column up by name.

Rename anyway?`,
      );
      if (!ok) { setRenamingId(null); return; }
    }
    void run(async () => {
      await api.patch(`/areas/${areaId}/stages/${stageId}`, { name });
      setRenamingId(null);
    }, 'Could not rename the stage.');
  };

  const move = (idx: number, dir: -1 | 1) => {
    const order = stages.map((s) => s.id);
    const j = idx + dir;
    if (j < 0 || j >= order.length) return;
    [order[idx], order[j]] = [order[j], order[idx]];
    void run(() => api.post(`/areas/${areaId}/stages/reorder`, { orderedIds: order }), 'Could not reorder.');
  };

  const del = (s: Stage) => {
    const warn = inPipeline(s.name)
      ? `"${s.name}" is part of this team's automatic stage advancement.

Deleting it will change where approvals send cards.

Delete anyway?`
      : `Delete the "${s.name}" stage?`;
    if (!window.confirm(warn)) return;
    void run(() => api.delete(`/areas/${areaId}/stages/${s.id}`), 'Could not delete the stage.');
  };

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/60 animate-fade-in" onClick={onClose} />
      <div className="fixed inset-x-4 top-16 z-50 mx-auto max-w-md overflow-hidden rounded-xl border border-line bg-canvas shadow-2xl animate-fade-in">
        <div className="flex h-11 items-center justify-between border-b border-line px-4">
          <span className="text-sm font-semibold text-ink">Edit stages</span>
          <button onClick={onClose} className="rounded p-1 text-muted hover:bg-hover hover:text-ink" title="Close (Esc)"><Icon i={X} size={14} /></button>
        </div>
        <div className="max-h-[60vh] overflow-y-auto p-3">
          {stages.map((s, i) => {
            const count = taskCountByStage.get(s.id) ?? 0;
            return (
              <div key={s.id} className="mb-1.5 flex items-center gap-2 rounded-md border border-line bg-page px-2 py-1.5">
                <span className="flex flex-col">
                  <button onClick={() => move(i, -1)} disabled={busy || i === 0} className="text-faint hover:text-ink disabled:opacity-20" title="Move up"><Icon i={ChevronUp} size={14} /></button>
                  <button onClick={() => move(i, 1)} disabled={busy || i === stages.length - 1} className="text-faint hover:text-ink disabled:opacity-20" title="Move down"><Icon i={ChevronDown} size={14} /></button>
                </span>
                {renamingId === s.id ? (
                  <input
                    autoFocus
                    value={renameDraft}
                    onChange={(e) => setRenameDraft(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') saveRename(s.id); else if (e.key === 'Escape') setRenamingId(null); }}
                    onBlur={() => saveRename(s.id)}
                    className="min-w-0 flex-1 rounded border border-brand bg-canvas px-1.5 py-0.5 text-[13px] text-ink outline-none"
                  />
                ) : (
                  <button onClick={() => { setRenamingId(s.id); setRenameDraft(s.name); }} className="min-w-0 flex-1 truncate text-left text-[13px] text-ink hover:underline" title={inPipeline(s.name) ? 'Rename — used by automatic stage advancement' : 'Rename'}>
                    {s.name}
                    {inPipeline(s.name) && <span className="ml-1 text-brand" title="Part of the approval pipeline"><Icon i={Zap} size={14} className="inline align-[-0.2em]" /></span>}
                  </button>
                )}
                {canSetOwners ? (
                <select
                  value={s.ownerId ?? ''}
                  onChange={(e) => setOwner(s.id, e.target.value ? Number(e.target.value) : null)}
                  disabled={busy}
                  className={`w-[92px] shrink-0 rounded border border-line bg-canvas px-1 py-0.5 text-[11px] outline-none ${s.ownerId ? 'text-ink' : 'text-faint'}`}
                  title="Whose step is this? A card landing here is added to their pile automatically."
                >
                  <option value="">nobody's</option>
                  {s.ownerId != null && !members.some((m) => m.id === s.ownerId) && (
                    <option value={s.ownerId}>{s.ownerName ?? `uid ${s.ownerId}`}</option>
                  )}
                  {members.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
                ) : s.ownerId != null && (
                  <span className="max-w-[92px] shrink-0 truncate text-[11px] text-muted" title="Whose step this is. The board's lead sets it.">{s.ownerName ?? 'owned'}</span>
                )}
                <span className="shrink-0 rounded bg-hover px-1.5 text-[11px] text-muted" title="Open tasks in this stage">{count}</span>
                <button
                  onClick={() => del(s)}
                  disabled={busy || count > 0}
                  className="shrink-0 text-faint hover:text-danger disabled:opacity-20"
                  title={count > 0 ? 'Move its tasks out first' : 'Delete stage'}
                ><Icon i={Trash2} size={14} /></button>
              </div>
            );
          })}
          <div className="mt-2 flex items-center gap-2">
            <input
              value={addDraft}
              onChange={(e) => setAddDraft(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') addStage(); }}
              placeholder="New stage name…  (Enter to add)"
              className="min-w-0 flex-1 rounded-md border border-line bg-page px-2 py-1.5 text-[13px] text-ink outline-none placeholder:text-faint focus:border-brand"
            />
            <button onClick={addStage} disabled={busy || !addDraft.trim()} className="flex items-center gap-1 rounded-md bg-brand px-3 py-1.5 text-xs font-semibold text-page disabled:opacity-40"><Icon i={Plus} size={14} /> Add</button>
          </div>
          <p className="mt-2 text-[11px] text-faint">Tap a name to rename · the arrows to reorder · stages with tasks can’t be deleted. {canSetOwners ? 'Give a step an owner and cards landing there are added to their pile automatically' : 'The board’s lead can give a step an owner, so cards landing there go to that person automatically'} — see “What happens by itself” in the project menu.</p>
        </div>
      </div>
    </>
  );
}
